import {
  AfterViewChecked,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { OverlayContainer } from '@angular/cdk/overlay';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, Subscription } from 'rxjs';
import {
  PLATFORM_DOCK_SIGN_OUT_ID,
  PLATFORM_EXIT_TO_MILINCO_ID,
  PLATFORM_NAV_MODULE_ID,
  POS_RAIL_EXIT_ID,
} from '@core/shell/admin-shell-mode.model';
import { ADMIN_SHELL_NAV_ICONS } from '@core/shell/admin-shell-nav-icons';
import { AdminShellModeService } from '@core/shell/admin-shell-mode.service';
import { SignOutService } from '@core/auth/sign-out.service';
import { NavItem } from 'src/app/layouts/full/vertical/sidebar/nav-item/nav-item';
import {
  defaultRouteForModule,
  defaultTenantRouteForModule,
  moduleHasSubnav,
  routesForModule,
} from 'src/app/layouts/full/vertical/sidebar/sidebar-data';
import { FormSaveBarOverlayService } from '@shared/ui/milinco-form-save-bar/form-save-bar-overlay.service';
import { confirmExitPos } from '@shared/ui/confirm-open-tab-dialog/confirm-enter-pos';
import { ToastService } from '@shared/ui/toast/toast.service';
import { AdminKeyboardInsetService } from './admin-keyboard-inset.service';
import { boostDockLauncherOverlay } from './dock-launcher-overlay.util';
import {
  MilincoMobileDockLauncherComponent,
  MobileDockLauncherItem,
} from './milinco-mobile-dock-launcher.component';
import { NavIconPairComponent } from '@shared/ui/nav-icon-pair/nav-icon-pair.component';

export interface MobileDockMenuItem {
  id: number;
  title: string;
  iconName: string;
}

const HUB_PREFERRED_IDS = [1, 2, 4] as const;
/** Umbral px para considerar overflow / borde de scroll. */
const SCROLL_EDGE_PX = 2;

@Component({
  selector: 'app-milinco-mobile-dock',
  standalone: true,
  imports: [TranslateModule, RouterLink, RouterLinkActive, NavIconPairComponent],
  templateUrl: './milinco-mobile-dock.component.html',
  styleUrl: './milinco-mobile-dock.component.scss',
  host: {
    class: 'milinco-mobile-dock-host',
    '[class.milinco-mobile-dock-host--hidden]': 'isHidden()',
    '[attr.aria-hidden]': 'isHidden() ? true : null',
  },
})
export class MilincoMobileDockComponent implements OnInit, OnDestroy, AfterViewChecked {
  private readonly router = inject(Router);
  private readonly bottomSheet = inject(MatBottomSheet);
  private readonly dialog = inject(MatDialog);
  private readonly overlayContainer = inject(OverlayContainer);
  private readonly translate = inject(TranslateService);
  private readonly saveBarOverlay = inject(FormSaveBarOverlayService);
  private readonly keyboardInset = inject(AdminKeyboardInsetService);
  private readonly adminShellMode = inject(AdminShellModeService);
  private readonly signOutService = inject(SignOutService);
  private readonly toast = inject(ToastService);
  private routerSub?: Subscription;
  private detachKeyboard?: () => void;
  private sectionsResizeObserver?: ResizeObserver;
  private lastWorkshopLinkCount = -1;

  readonly sectionsEl = viewChild<ElementRef<HTMLElement>>('sectionsEl');

  readonly menuItems = input<MobileDockMenuItem[]>([]);
  readonly navItems = input<NavItem[]>([]);
  readonly selectedModuleId = input<number | null>(null);
  /** Shell POS: rail plano abajo (todas las rutas + Salir a la derecha). */
  readonly posShell = input(false);

  readonly moduleSelected = output<number>();
  /** Tras salir del POS con éxito (misma señal que el rail). */
  readonly posExitCompleted = output<void>();

  private readonly routerUrl = signal(this.router.url);

  /**
   * Venta con ?table=&area= → sesión de mesa: el dock resalta Mesas como contexto
   * (la ruta activa sigue siendo Venta).
   */
  readonly inPosMesaSession = computed(() => {
    const url = this.routerUrl();
    const path = url.split('?')[0];
    if (!/(^|\/)venta\/?$/.test(path)) {
      return false;
    }
    const query = url.includes('?') ? url.slice(url.indexOf('?') + 1) : '';
    const params = new URLSearchParams(query);
    return !!params.get('table')?.trim() && !!params.get('area')?.trim();
  });

  readonly keyboardOpen = computed(() => this.keyboardInset.bottomInsetPx() > 0);
  readonly saveBarShowing = this.saveBarOverlay.showing;

  readonly isHidden = computed(() => this.keyboardOpen() || this.saveBarShowing());

  /**
   * Taller: módulo actual con ≥2 secciones, o shell POS (rail plano).
   * Secciones a la izquierda (repartidas o scrolleables); Menú fijo a la derecha.
   */
  readonly isWorkshop = computed(
    () =>
      this.posShell() || moduleHasSubnav(this.navItems(), this.selectedModuleId())
  );

  readonly canScrollLeft = signal(false);
  readonly canScrollRight = signal(false);
  /** Hay overflow real → mostrar flechas de scroll. */
  readonly sectionsOverflow = signal(false);

  readonly hubSlots = computed((): MobileDockMenuItem[] => {
    const items = this.menuItems();
    const byId = new Map(items.map((item) => [item.id, item]));
    const slots: MobileDockMenuItem[] = [];
    for (const id of HUB_PREFERRED_IDS) {
      const hit = byId.get(id);
      if (hit) {
        slots.push(hit);
      }
      if (slots.length >= 3) {
        break;
      }
    }
    if (slots.length < 3) {
      for (const item of items) {
        if (slots.some((s) => s.id === item.id)) {
          continue;
        }
        slots.push(item);
        if (slots.length >= 3) {
          break;
        }
      }
    }
    return slots;
  });

  readonly isPlatformShell = computed(() => this.adminShellMode.mode() === 'platform');

  readonly canOpenPlatform = computed(
    () =>
      !this.posShell() &&
      this.adminShellMode.mode() === 'tenant' &&
      this.adminShellMode.isSuperadminSession()
  );

  /**
   * Menú (launcher):
   * - POS: siempre (Salir en el sheet).
   * - Platform: siempre (Ir a Milinco + Salir; el rail solo tiene 1 módulo).
   * - Tenant: solo si hay más de un módulo en el rail.
   */
  readonly showDockMenu = computed(
    () => this.posShell() || this.isPlatformShell() || this.canOpenPlatform() || this.menuItems().length > 1
  );

  /** Columnas del hub = slots (+ Menú si aplica). */
  readonly hubColumnCount = computed(
    () => this.hubSlots().length + (this.showDockMenu() ? 1 : 0)
  );

  readonly workshopLinks = computed(() => {
    if (this.posShell()) {
      return this.menuItems()
        .filter((item) => item.id !== POS_RAIL_EXIT_ID)
        .map((item) => {
          const route = defaultRouteForModule(this.navItems(), item.id);
          if (!route) {
            return null;
          }
          return {
            route,
            label: item.title,
            iconName: item.iconName || 'solar:widget-2-line-duotone',
          };
        })
        .filter(
          (link): link is { route: string; label: string; iconName: string } => link != null
        );
    }
    const moduleId = this.selectedModuleId();
    if (moduleId == null) {
      return [];
    }
    const module = this.navItems().find((m) => m.id === moduleId);
    const children = module?.children ?? [];
    return children
      .filter(
        (child): child is NavItem & { route: string; displayName: string } =>
          !!child.route?.trim() &&
          !!child.displayName?.trim() &&
          !child.openPosInNewTab &&
          !child.openInNewTab
      )
      .map((child) => ({
        route: child.route.startsWith('/') ? child.route : `/${child.route}`,
        label: child.displayName,
        iconName: child.iconName ?? 'solar:widget-2-line-duotone',
      }));
  });

  readonly launcherItems = computed((): MobileDockLauncherItem[] => {
    const hubIds = new Set(this.hubSlots().map((s) => s.id));
    return this.menuItems()
      .filter((item) => !hubIds.has(item.id))
      .map((item) => ({
        id: item.id,
        title: item.title,
        iconName: item.iconName,
      }));
  });

  constructor() {
    afterNextRender(() => {
      this.bindSectionsObserver();
      this.updateScrollHints();
    });
  }

  ngOnInit(): void {
    this.detachKeyboard = this.keyboardInset.attach();
    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        this.routerUrl.set(e.urlAfterRedirects);
        queueMicrotask(() => this.updateScrollHints());
      });
    /* Iconos ~24px + labels 12px; sin eyebrow (contexto = título de página, patrón nativo). */
    document.documentElement.style.setProperty('--milinco-bottom-chrome', '4.5rem');
  }

  ngAfterViewChecked(): void {
    const count = this.workshopLinks().length;
    if (count !== this.lastWorkshopLinkCount) {
      this.lastWorkshopLinkCount = count;
      this.bindSectionsObserver();
      this.updateScrollHints();
    }
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    this.detachKeyboard?.();
    this.sectionsResizeObserver?.disconnect();
    document.documentElement.style.removeProperty('--milinco-bottom-chrome');
  }

  /** Un toque = ir al módulo (ruta por defecto). Sin sheet intermedio de secciones. */
  selectHub(item: MobileDockMenuItem): void {
    if (item.id === PLATFORM_NAV_MODULE_ID && !this.isPlatformShell()) {
      if (this.canOpenPlatform()) {
        this.adminShellMode.enterPlatform();
      }
      return;
    }
    if (item.id === POS_RAIL_EXIT_ID) {
      this.moduleSelected.emit(item.id);
      confirmExitPos(this.dialog).subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }
        void this.adminShellMode.exitPos().then((ok) => {
          if (!ok) {
            this.toast.infoTranslated('pos.shell.exitCancelled');
            return;
          }
          this.posExitCompleted.emit();
        });
      });
      return;
    }
    if (item.id === PLATFORM_EXIT_TO_MILINCO_ID) {
      this.adminShellMode.toggleContextSwitch();
      return;
    }
    if (item.id === PLATFORM_DOCK_SIGN_OUT_ID) {
      this.signOutService.signOut();
      return;
    }
    this.moduleSelected.emit(item.id);
    const route = this.posShell()
      ? defaultRouteForModule(this.navItems(), item.id)
      : (defaultTenantRouteForModule(this.navItems(), item.id) ??
        routesForModule(this.navItems(), item.id)[0] ??
        null);
    if (route) {
      void this.router.navigateByUrl(route);
    }
  }

  openMore(): void {
    if (!this.showDockMenu()) {
      return;
    }
    // POS: Salir/Milinco solo en el sheet (no en el slot derecho del pulgar).
    // Platform: Ir a Milinco + Salir (el rail solo tiene 1 módulo; secciones ya están en el dock).
    let all: MobileDockLauncherItem[];
    if (this.posShell()) {
      all = this.menuItems().map((item) => ({
        id: item.id,
        title: item.title,
        iconName: item.iconName,
      }));
    } else if (this.isPlatformShell()) {
      all = [
        {
          id: PLATFORM_EXIT_TO_MILINCO_ID,
          title: 'shell.goToMilinco',
          iconName: ADMIN_SHELL_NAV_ICONS.switchToMilinco,
        },
        {
          id: PLATFORM_DOCK_SIGN_OUT_ID,
          title: 'header.profile.menu.signOut',
          iconName: 'solar:logout-2-line-duotone',
        },
      ];
    } else {
      all = [
        ...this.hubSlots().map((item) => ({
          id: item.id,
          title: item.title,
          iconName: item.iconName,
        })),
        ...this.launcherItems(),
      ];
      if (this.canOpenPlatform() && !all.some((item) => item.id === PLATFORM_NAV_MODULE_ID)) {
        all.push({
          id: PLATFORM_NAV_MODULE_ID,
          title: 'shell.goToPlatform',
          iconName: ADMIN_SHELL_NAV_ICONS.switchToPlatform,
        });
      }
    }
    const clearOverlayBoost = boostDockLauncherOverlay(this.overlayContainer);
    const ref = this.bottomSheet.open(MilincoMobileDockLauncherComponent, {
      data: {
        items: all,
        titleKey: 'shell.dock.moreTitle',
        activeId: this.selectedModuleId(),
      },
      ariaLabel: this.translate.instant('shell.dock.moreTitle'),
      panelClass: ['milinco-admin-bottom-sheet', 'milinco-mobile-dock-launcher-pane'],
    });
    ref.afterDismissed().subscribe((id) => {
      clearOverlayBoost();
      if (id == null) {
        return;
      }
      const item = all.find((entry) => entry.id === id);
      if (item) {
        this.selectHub(item);
      }
    });
  }

  isHubActive(item: MobileDockMenuItem): boolean {
    return this.selectedModuleId() === item.id;
  }

  /** Ruta del plano Mesas en shell POS o tenant embebido. */
  isMesasDockRoute(route: string): boolean {
    const normalized = route.split('?')[0].replace(/\/+$/, '');
    return normalized.endsWith('/mesas');
  }

  showMesaContextOnLink(route: string): boolean {
    return this.inPosMesaSession() && this.isMesasDockRoute(route);
  }

  onSectionsScroll(): void {
    this.updateScrollHints();
  }

  scrollSections(direction: -1 | 1): void {
    const el = this.sectionsEl()?.nativeElement;
    if (!el) {
      return;
    }
    const delta = Math.max(el.clientWidth * 0.75, 120) * direction;
    el.scrollBy({ left: delta, behavior: 'smooth' });
  }

  private bindSectionsObserver(): void {
    this.sectionsResizeObserver?.disconnect();
    const el = this.sectionsEl()?.nativeElement;
    if (!el || typeof ResizeObserver === 'undefined') {
      return;
    }
    this.sectionsResizeObserver = new ResizeObserver(() => this.updateScrollHints());
    this.sectionsResizeObserver.observe(el);
  }

  updateScrollHints(): void {
    const el = this.sectionsEl()?.nativeElement;
    if (!el) {
      this.canScrollLeft.set(false);
      this.canScrollRight.set(false);
      this.sectionsOverflow.set(false);
      return;
    }
    const maxScroll = el.scrollWidth - el.clientWidth;
    const overflow = maxScroll > SCROLL_EDGE_PX;
    this.sectionsOverflow.set(overflow);
    this.canScrollLeft.set(overflow && el.scrollLeft > SCROLL_EDGE_PX);
    this.canScrollRight.set(overflow && el.scrollLeft < maxScroll - SCROLL_EDGE_PX);
  }
}
